import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {ExchangeRate, Fund} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface ExchangeRateEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  exchangeRates: ExchangeRate[];
  funds: Fund[];
  exchangeRate?: ExchangeRate;
}

export function ExchangeRateEditor(
  props: ExchangeRateEditorProps,
): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();

  const [validating, setValidating] = useState(false);

  const saveExchangeRate = useMutation(
    (exchangeRate: ExchangeRate) => {
      if (props.exchangeRate === undefined) {
        // console.log(
        //   'adding exchangeRate: ' + JSON.stringify(exchangeRate, null, 2),
        // );
        return (
          firestore()
            .collection('exchangeRates')
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(exchangeRate) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving exchangeRate: ' + JSON.stringify(exchangeRate, null, 2),
      // );
      return firestore()
        .collection('exchangeRates')
        .doc(props.exchangeRate?.ref.id)
        .set(exchangeRate, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries('exchangeRates');
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let exchangeRate: {[key: string]: FormikStoreType} = {
    exchangeRateId: Date.now(),
    dateRangeStart: new Date(),
    dateRangeEnd: new Date(),
    originFundId: undefined,
    destinationFundId: undefined,
    rate: '1.000000',
  };
  if (props.exchangeRate !== undefined) {
    exchangeRate.exchangeRateId = props.exchangeRate.exchangeRateId;
    exchangeRate.dateRangeStart = (
      props.exchangeRate.dateRangeStart as FirebaseFirestoreTypes.Timestamp
    ).toDate();
    exchangeRate.dateRangeEnd = (
      props.exchangeRate.dateRangeEnd as FirebaseFirestoreTypes.Timestamp
    ).toDate();
    exchangeRate.originFundId = props.exchangeRate.originFundId;
    exchangeRate.destinationFundId = props.exchangeRate.destinationFundId;
    exchangeRate.exchangeRate = props.exchangeRate.exchangeRate;
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.exchangeRate ? 'Edit' : 'Create'} Exchange Rate`,
              firestoreObject: exchangeRate,
              fieldMetadata: [
                {
                  key: 'exchangeRateId',
                  name: 'Exchange Rate ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Exchange Rate ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'dateRangeStart',
                  name: 'Start Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  errorText: 'Please select a date',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'dateRangeEnd',
                  name: 'End Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  errorText: 'Please select a date',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'originFundId',
                  name: 'Origin Fund',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText: 'Origin Fund is a required field',
                  selectItems: {
                    documents: props.funds,
                    items: props.funds.map(fund => fund.ref),
                    labelExtractor: fund => fund?.fundCode,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'destinationFundId',
                  name: 'Destination Fund',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText: 'Destination Fund is a required field',
                  selectItems: {
                    documents: props.funds,
                    items: props.funds.map(fund => fund.ref),
                    labelExtractor: fund => fund?.fundCode,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'exchangeRate',
                  name: "Exchange Rate (format: 'NN.NNNNNN')",
                  type: FormikStoreTypes.number,
                  required: true,
                  validator: (value, required, formikStoreObjects) => {
                    // FIXME maybe? yup.number() validator strips trailing zeros. We want them.
                    // The trailing zeros are still in the formikStore object
                    const numberString = formikStoreObjects[0].firestoreObject
                      .exchangeRate as string;
                    // console.log('firestoreObject ' + numberString);
                    const numberComponents = numberString?.split('.');
                    if (
                      numberComponents === undefined ||
                      (numberComponents.length > 1 &&
                        numberComponents[1].length < 6)
                    ) {
                      return false;
                    }
                    if (
                      isNaN(parseFloat(numberString)) ||
                      Number.isNaN(numberString)
                    ) {
                      return false;
                    }
                    return DefaultFormikStoreValidator(
                      value,
                      required,
                      formikStoreObjects,
                    );
                  },
                  errorText:
                    'A number with at least 6 decimal places is required (for rounding precision)',
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveExchangeRate.mutate(
                  formObjects[0].firestoreObject as unknown as ExchangeRate,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
